import React from 'react';
import { PrimaryButton } from '@magoosh/lib/button';
import { paths } from 'config/path_helpers';
import DropdownList from 'react-widgets/lib/DropdownList';
import moment from 'moment';
import style from './style.module.scss';
import { Alert } from 'react-bootstrap';

interface Section {
  id: number;
  name: string;
  templateId: number;
  templateName: string;
  displayOrder: number;
  lastTaken: Date;
  hasExplanations: boolean;
}

interface SectionTestsPageProps {
  authenticityToken: string;
  sectionsByName: StringHash<Section[]>;
}

interface SectionSelectorProps {
  sections: Section[];
  authenticityToken: string;
}

class SectionSelector extends React.Component<SectionSelectorProps> {
  state = {
    selectedSectionId: '',
    selectedTemplateId: ''
  };

  render() {
    return (
      <div className={style.sectionSelector}>
        <div className={style.dropdown}>
          <DropdownList
            data={this.props.sections}
            itemComponent={(item) => {
              const section = item.value;

              if (section.lastTaken) {
                return <span className={style.taken}>{item.text}</span>;
              }

              return item.text;
            }}
            textField={(section) => {
              if (!section) return '';

              let text = `${section.templateName} ${section.name} - Section ${section.displayOrder}`;

              if (!section.hasExplanations) {
                text += ' *';
              }

              if (section.lastTaken) {
                text += ` (last taken ${moment(section.lastTaken).format('L')})`;
              }
              return text;
            }}
            onChange={(value) => {
              if (!value) {
                this.setState({
                  selectedSectionId: null,
                  selectedTemplateId: null
                });
              } else {
                this.setState({
                  selectedSectionId: value.id,
                  selectedTemplateId: value.templateId
                });
              }
            }}
            placeholder="Select an exam section..."
          />
        </div>
        <div className={style.button}>
          <form action={paths.practiceTests()} method="POST">
            <input type="hidden" name="mock_test[template_id]" value={this.state.selectedTemplateId} />
            <input
              type="hidden"
              name="mock_test[mock_test_section_id]"
              value={this.state.selectedSectionId}
            />
            <input type="hidden" name="authenticity_token" value={this.props.authenticityToken} />
            <PrimaryButton
              disabled={!this.state.selectedSectionId || !this.state.selectedTemplateId}
              type="submit">
              Start the Section
            </PrimaryButton>
          </form>
        </div>
      </div>
    );
  }
}

export const SectionTestsPage: React.FC<SectionTestsPageProps> = (props) => {
  const renderPremiumSubscriptionAlert = function () {
    if (props.sectionsByName && Object.keys(props.sectionsByName).length > 0) {
      return null;
    }

    return (
      <Alert className={style.centered}>
        Magoosh practice sections require that you have a Premium subscription. To take practice sections,{' '}
        <a href={paths.plans()}>upgrade your subscription</a>.
      </Alert>
    );
  };

  return (
    <>
      {renderPremiumSubscriptionAlert()}
      <h1>Start a Timed Practice Section</h1>
      <p>
        Select an official PrepTest section to practice below. Each section is a standard 35-minute timed
        section.
      </p>
      <p>
        If you want to take a section with extra time, you can go to your profile and click &quot;Edit Account
        Settings&quot; to change your setting.
      </p>
      <p>
        <strong>*Important note:</strong> Sections marked with an asterisk in the drop-down menus do not yet
        have complete video and written explanations. We are working on creating explanations as soon as
        possible!
      </p>
      {Object.keys(props.sectionsByName).map((sectionName) => (
        <React.Fragment key={sectionName}>
          <h3>{sectionName}</h3>
          <SectionSelector
            authenticityToken={props.authenticityToken}
            sections={props.sectionsByName[sectionName]}
          />
        </React.Fragment>
      ))}
    </>
  );
};

export default SectionTestsPage;
